import React from "react";
import axios from "axios";
import { Loader2, Save, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tennis`;
export const STATUS_CLS = {
  APPROVED: "bg-emerald-600 text-white", WATCH: "bg-sky-900/80 text-sky-200", PASS: "bg-slate-800 text-slate-400",
  "DATA UNAVAILABLE": "bg-amber-950/70 text-amber-300",
};
const STAGE_CLS = { OK: "text-lime-300", "NO MEETINGS": "text-slate-400", "DATA UNAVAILABLE": "text-amber-300", SKIPPED: "text-slate-500", LOW: "text-lime-300", MEDIUM: "text-amber-300", HIGH: "text-rose-400" };

const Stage = ({ s, i }) => (
  <div className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-950/60 p-2.5" data-testid={`gb-stage-${s.key}`}>
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-lime-400/15 font-mono text-[10px] font-bold text-lime-300">{i + 1}</span>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display text-[12px] font-bold uppercase text-slate-100">{s.label}</span>
        <span className={`font-mono text-[10px] font-bold ${STAGE_CLS[s.status] || "text-slate-300"}`}>{s.status}</span>
        {s.p != null && <span className="ml-auto font-mono text-[11px] text-lime-200">P1 {Math.round(s.p * 100)}%</span>}
      </div>
      <p className="font-mono text-[10px] text-slate-400">{s.detail}{s.source ? ` · ${s.source}` : ""}</p>
    </div>
  </div>
);

const FormList = ({ name, f }) => (
  <div className="rounded-lg border border-slate-800 p-2" data-testid={`gb-form-${name.replace(/\W+/g, "-").toLowerCase()}`}>
    <p className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">{name} · last 10 {f ? `${f.l10_w}-${f.l10_n - f.l10_w} · ${f.streak} · games won ${f.games_won_pct}%` : "— no stored results"}</p>
    {(f?.recent || []).map((x) => (
      <div key={x.id} className="flex justify-between gap-2 font-mono text-[10px]">
        <span className={x.won ? "text-lime-300" : "text-rose-400"}>{x.won ? "W" : "L"}</span>
        <span className="flex-1 truncate text-slate-300">{x.opp}</span>
        <span className="text-slate-500">{x.score}{x.retired ? " ret" : ""}</span>
        <span className="text-slate-600">{x.surface === "UNKNOWN" ? "—" : x.surface} · {x.date.slice(5)}</span>
      </div>
    ))}
  </div>
);

const H2HBox = ({ d, reload }) => {
  const [a, b] = d.match.players;
  const [w1, setW1] = React.useState(d.override?.p1_id === a.id ? d.override.p1_wins : d.override?.p2_wins ?? "");
  const [w2, setW2] = React.useState(d.override?.p1_id === a.id ? d.override.p2_wins : d.override?.p1_wins ?? "");
  const valid = /^\d+$/.test(String(w1)) && /^\d+$/.test(String(w2));
  const save = async () => {
    if (!valid) { toast.error("Enter both win counts from TennisTonic"); return; }
    try {
      await axios.post(`${API}/h2h-override`, { p1_id: a.id, p2_id: b.id, p1_name: a.name, p2_name: b.name, p1_wins: Number(w1), p2_wins: Number(w2) });
      toast.success("TennisTonic H2H saved"); reload();
    } catch { toast.error("Could not save H2H"); }
  };
  const clear = async () => {
    try {
      await axios.delete(`${API}/h2h-override`, { params: { p1_id: a.id, p2_id: b.id } });
      setW1(""); setW2(""); toast.success("Override removed"); reload();
    } catch { toast.error("Could not remove override"); }
  };
  const h = d.eval.h2h;
  return (
    <div className="rounded-lg border border-lime-900/50 bg-lime-950/10 p-3" data-testid="gb-h2h">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-display text-[13px] font-bold uppercase text-lime-200">Head to head · {a.short} {h.p1}-{h.p2} {b.short}</p>
      </div>
      <p className="font-mono text-[10px] text-slate-500">Source: {h.url ? <a href={h.url} target="_blank" rel="noreferrer" data-testid="gb-h2h-public-link" className="text-lime-300 underline">{h.source}</a> : h.source}. Enter a career H2H below to override.</p>
      {h.meetings.map((x) => <p key={x.id} className="font-mono text-[10px] text-slate-400">{x.date} · {x.tournament} · {x.won ? a.short : b.short} won {x.score}</p>)}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input data-testid="gb-h2h-p1" value={w1} onChange={(e) => setW1(e.target.value)} placeholder={`${a.short} wins`} className="h-9 w-28 rounded-md border border-slate-700 bg-slate-950 px-2 font-mono text-xs text-slate-100" />
        <input data-testid="gb-h2h-p2" value={w2} onChange={(e) => setW2(e.target.value)} placeholder={`${b.short} wins`} className="h-9 w-28 rounded-md border border-slate-700 bg-slate-950 px-2 font-mono text-xs text-slate-100" />
        <button data-testid="gb-h2h-save" onClick={save} className="flex h-9 items-center gap-1 rounded-md bg-lime-500 px-3 font-mono text-[11px] font-bold text-slate-950 hover:bg-lime-400"><Save className="h-3.5 w-3.5" /> Save override</button>
        {d.override && <button data-testid="gb-h2h-clear" onClick={clear} className="flex h-9 items-center gap-1 rounded-md border border-slate-700 px-3 font-mono text-[11px] text-slate-300 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /> Remove</button>}
      </div>
    </div>
  );
};

export const MatchDialog = ({ matchId, date, onClose }) => {
  const [d, setD] = React.useState(null);
  const load = React.useCallback(() => {
    axios.get(`${API}/match/${matchId}`, { params: { date } }).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [matchId, date]);
  React.useEffect(() => { if (matchId) { setD(null); load(); } }, [matchId, load]);
  const e = d?.eval;
  return (
    <Dialog open={!!matchId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-slate-800 bg-slate-950 text-slate-100" data-testid="gb-match-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-wide text-lime-200">{d?.match ? `${d.match.players[0].name} vs ${d.match.players[1].name}` : "Match"}</DialogTitle></DialogHeader>
        {!d ? <div className="flex items-center gap-2 py-8 font-mono text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin text-lime-400" /> Running GREEN BALL…</div>
          : d.error ? <p className="font-mono text-xs text-rose-400">Match not available.</p> : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 font-mono text-[11px] text-slate-400">
              <span>{d.match.tour} · {d.match.tournament} · {d.match.round} · {d.match.surface}</span>
              <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${STATUS_CLS[e.status]}`} data-testid="gb-final-status">FINAL STATUS: {e.status}</span>
            </div>
            {d.match.state !== "pre" && (
              <p className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 font-mono text-[11px] text-slate-300" data-testid="gb-frozen">
                {d.frozen ? `Pre-match pick (frozen): ${d.frozen.pick} ${Math.round(d.frozen.prob * 100)}% · ${d.frozen.status}${d.frozen.result ? ` · ${d.frozen.result}` : ""}` : "No pre-match pick was frozen for this match; the analysis below is a retrospective view."}
              </p>
            )}
            <p className="font-display text-lg font-bold text-lime-300" data-testid="gb-pick">{d.match.state === "pre" ? "" : "Retro view · "}{e.pick} · {Math.round(e.prob * 100)}%</p>
            <div className="space-y-1.5">{e.stages.map((s, i) => <Stage key={s.key} s={s} i={i} />)}</div>
            <div className="grid grid-cols-2 gap-1.5 font-mono text-[10px] sm:grid-cols-4" data-testid="gb-gates">
              {Object.entries(e.gates).map(([k, v]) => <span key={k} className={`rounded px-2 py-1 ${v ? "bg-lime-950/50 text-lime-300" : "bg-rose-950/40 text-rose-300"}`}>{v ? "✓" : "✗"} {k.replace("_", " ")}</span>)}
            </div>
            <div className="rounded-lg border border-slate-800 p-2.5 font-mono text-[11px] text-slate-300" data-testid="gb-totals">
              Total games projection: <b className="text-lime-200">{e.totals.projection ?? "—"}</b> · {d.match.players[0].short} avg {e.totals.avg_a ?? "—"} ({e.totals.n_a}) · {d.match.players[1].short} avg {e.totals.avg_b ?? "—"} ({e.totals.n_b}) · serve / return / aces / DF / breaks: DATA UNAVAILABLE
            </div>
            <H2HBox d={d} reload={load} />
            <div className="grid gap-2 sm:grid-cols-2">
              <FormList name={d.match.players[0].name} f={e.form.p1} />
              <FormList name={d.match.players[1].name} f={e.form.p2} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MatchDialog;
